import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import ExerciseSelectorGrid from './ExerciseSelectorGrid';
import TemplateSelectorPanel from './TemplateSelectorPanel';
import LastSessionsHint, { useLastSessionsHints } from './LastSessionsHint';
import { getExerciseAnimationUrl } from '../../utils/exerciseImages';
import { estimateWorkoutSeconds, formatDuration } from '../../utils/workoutDuration';
import { isCardio, secondsToMMSS, mmssToSeconds } from '../../utils/cardio';
import { MuscleHeatmapCard } from './MuscleHeatmapCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// VALIDATION LIMITS FOR WORKOUT PLANNING
// ============================================
const VALIDATION_LIMITS = {
  target_sets: { min: 1, max: 20, label: 'sets' },
  target_reps: { min: 1, max: 100, label: 'reps' },
  target_weight: { min: 0, max: 500, label: 'weight (kg)' },
  rest_time: { min: 15, max: 600, label: 'rest (sec)' }
};

// ============================================
// FULL-SCREEN WORKOUT EDITOR (UNIFIED)
// Supports modes: plan | entry | results | template
// ============================================

const FullScreenWorkoutEditor = ({
  booking,
  workout,
  mode, // 'plan' | 'entry' | 'results' | 'template'
  exercises: allExercises = [],
  templates,
  onSave,
  onClose,
  onFinishWorkout, // Handler to finish workout
  templateName: initialTemplateName, // For template mode
  clientId, // For fetching exercise usage history
  t
}) => {
  const [workoutExercises, setWorkoutExercises] = useState([]);
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showMuscleBalance, setShowMuscleBalance] = useState(false);
  const [workoutTimes, setWorkoutTimes] = useState({ total_time: 0, rest_time: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [draggedExercise, setDraggedExercise] = useState(null);
  const [templateName, setTemplateName] = useState(initialTemplateName || '');
  const [appliedTemplateOrigin, setAppliedTemplateOrigin] = useState(null);

  // Trainer-side deload marker (operational continuity signal).
  // Persists to workout.is_deload via PUT /workouts/{id}.
  // NOT shown in template-edit mode (templates have no week to anchor).
  const [isDeload, setIsDeload] = useState(false);

  // Exercise usage history for instance mode prefill
  const [exerciseUsage, setExerciseUsage] = useState({});
  
  // Validation errors state: { exerciseIndex: { field: 'error message' } }
  const [validationErrors, setValidationErrors] = useState({});

  // Close the exercise-picker modal with the Escape key.
  useEffect(() => {
    if (!showExerciseSelector) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowExerciseSelector(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showExerciseSelector]);

  // P0-1: Last-time hints — last 3 sessions per exercise + PR + suggestion
  // Skip in template mode (templates are not client-specific) and when clientId is missing.
  const allExerciseIdsForHints = useMemo(
    () => (workoutExercises || []).map((ex) => ex?.exercise_id).filter(Boolean),
    [workoutExercises]
  );
  const { data: lastSessionsHints } = useLastSessionsHints(
    mode !== 'template' && clientId && clientId !== 'template' ? clientId : null,
    allExerciseIdsForHints,
    3
  );

  // MODE DEFINITIONS:
  // - plan: Full structure editing (future bookings) - shows sets/reps/weight/rest
  // - entry: Results entry mode - can log data, but NOT edit structure
  // - results: Correction mode - structure locked, can only edit logged data
  // - template: Template editing - structure editing allowed, but NO sets/reps/weight/rest inputs
  const isResultsMode = mode === 'results';
  const isEntryMode = mode === 'entry';
  const isPlanMode = mode === 'plan';
  const isTemplateMode = mode === 'template';
  
  // Structure is locked in entry and results modes (NOT template mode)
  const isStructureLocked = isEntryMode || isResultsMode;
  // In template mode, structure is editable but target parameters are hidden
  const canEditStructure = isPlanMode || isTemplateMode;
  // Can still edit execution data in entry and results modes
  const canEditExecutionData = !isTemplateMode; // Not in template mode
  
  // Fetch exercise usage data for instance mode
  useEffect(() => {
    const fetchExerciseUsage = async () => {
      // Only fetch in instance mode (plan/entry) and only if we have a client ID
      if (isTemplateMode || !clientId) {
        setExerciseUsage({});
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/exercises/client-usage?client_id=${clientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExerciseUsage(response.data || {});
      } catch (error) {
        console.error('Error fetching exercise usage:', error);
        setExerciseUsage({});
      }
    };
    
    fetchExerciseUsage();
  }, [clientId, isTemplateMode]);

  // Initialize exercises from workout
  useEffect(() => {
    if (workout?.exercises) {
      setWorkoutExercises(workout.exercises.map(ex => ({
        ...ex,
        actual_sets: ex.actual_sets || [],
        note: ex.note || '' // Per-exercise notes field
      })));
      setWorkoutTimes({
        total_time: workout.total_time || 0,
        rest_time: workout.rest_time || 0
      });
      // Set template name from workout if in template mode
      if (isTemplateMode && workout.name) {
        setTemplateName(workout.name || workout.template_name || '');
      }
      // Trainer-side deload flag — operational signal (continuity tier)
      setIsDeload(!!workout.is_deload);
    } else {
      setWorkoutExercises([]);
      setWorkoutTimes({ total_time: 0, rest_time: 0 });
      setIsDeload(false);
    }
  }, [workout, isTemplateMode]);

  // Warn user about unsaved changes when trying to close
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        onClose();
      }
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  // Handle browser back button / escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        // If the exercise-picker modal is open, Esc closes only the modal
        // (handled by its own listener) — never unmount the editor here.
        if (showExerciseSelector) return;
        handleClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, showExerciseSelector]);

  // Add exercise to workout (only in plan/template mode - structure locked otherwise)
  // NOTE: Does NOT auto-close selector - user can add multiple exercises
  // INSTANCE MODE: Uses exerciseUsage for intelligent prefill
  const addExercise = (exercise) => {
    if (isStructureLocked) return;

    // Get usage data for intelligent prefill (instance mode only)
    const usage = !isTemplateMode ? exerciseUsage[exercise.id] : null;
    
    // Cardio exercises (e.g. jump rope, treadmill) need their exercise_type
    // carried through. The active interface switches to cardio mode based on
    // `exercise_type === 'cardio'`. Without it, jump rope would render as a
    // strength exercise (reps × weight) which is wrong.
    const isCardio = exercise.exercise_type === 'cardio' || exercise.category === 'cardio';
    
    const newExercise = {
      exercise_id: exercise.id,
      exercise_name: exercise.name,
      exercise_name_es: exercise.name_es || exercise.spanish_name,
      // Both modes: use sensible defaults; user edits in template or at booking time
      target_sets: isTemplateMode ? 4 : 4,
      target_reps: isCardio ? 1 : (isTemplateMode ? 12 : 12),
      target_weight: isCardio ? 0 : (isTemplateMode ? 0 : (usage?.last_weight || 0)),
      target_duration: isCardio ? 60 : undefined,
      rest_time: isTemplateMode ? 120 : 120,
      instructions: exercise.instructions,
      instructions_es: exercise.instructions_es,
      image_url: exercise.image_url,
      animation_webp: exercise.animation_webp,
      muscle_groups: exercise.muscle_groups,
      difficulty: exercise.difficulty,
      equipment: exercise.equipment,
      // Modality classification — required for cardio mode + filters + analytics
      category: exercise.category,
      exercise_type: exercise.exercise_type,
      movement_pattern: exercise.movement_pattern,
      is_superset: false,
      superset_group: null,
      actual_sets: [],
      note: '' // Per-exercise notes
    };
    setWorkoutExercises([...workoutExercises, newExercise]);
    // Removed: setShowExerciseSelector(false) - keep selector open for adding multiple exercises
    setHasUnsavedChanges(true);
  };

  // Update exercise field
  const updateExercise = (index, field, value) => {
    // When structure is locked, only allow updating actual_sets and note
    const allowedFieldsWhenLocked = ['actual_sets', 'note'];
    if (isStructureLocked && !allowedFieldsWhenLocked.includes(field)) return;
    
    // In template mode, allow superset fields and notes but not target params
    const allowedFieldsForTemplate = ['is_superset', 'superset_group', 'note'];
    if (isTemplateMode && !allowedFieldsForTemplate.includes(field)) return;

    // Validate planning fields against limits
    const limits = VALIDATION_LIMITS[field];
    if (limits && !isNaN(value)) {
      const numValue = parseFloat(value);
      const newErrors = { ...validationErrors };
      
      if (!newErrors[index]) newErrors[index] = {};
      
      if (numValue < limits.min) {
        newErrors[index][field] = `Min ${limits.label}: ${limits.min}`;
      } else if (numValue > limits.max) {
        newErrors[index][field] = `Max ${limits.label}: ${limits.max}`;
      } else {
        // Clear error for this field
        delete newErrors[index][field];
        if (Object.keys(newErrors[index]).length === 0) {
          delete newErrors[index];
        }
      }
      
      setValidationErrors(newErrors);
    }

    const updated = [...workoutExercises];
    updated[index][field] = value;
    setWorkoutExercises(updated);
    setHasUnsavedChanges(true);
  };

  // Remove exercise (only in plan/template mode - structure locked otherwise)
  const removeExercise = (index) => {
    if (isStructureLocked) return;

    setWorkoutExercises(workoutExercises.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  };

  // Toggle superset (works in plan AND template mode - structure locked otherwise)
  const toggleSuperset = (index) => {
    if (isStructureLocked) return;

    const updated = [...workoutExercises];
    updated[index] = { ...updated[index], is_superset: !updated[index].is_superset };
    setWorkoutExercises(updated);
    setHasUnsavedChanges(true);
  };

  // Drag and drop for reordering (plan/template mode only - structure locked otherwise)
  const handleDragStart = (index) => {
    if (isStructureLocked) return;
    setDraggedExercise(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (isStructureLocked || draggedExercise === null || draggedExercise === index) return;
  };

  const handleDrop = (index) => {
    if (isStructureLocked || draggedExercise === null || draggedExercise === index) return;

    const updated = [...workoutExercises];
    const [removed] = updated.splice(draggedExercise, 1);
    updated.splice(index, 0, removed);
    setWorkoutExercises(updated);
    setDraggedExercise(null);
    setHasUnsavedChanges(true);
  };

  // PART 3 FIX: Touch-friendly move up/down
  const moveExercise = (fromIndex, direction) => {
    if (isStructureLocked) return;
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= workoutExercises.length) return;
    const updated = [...workoutExercises];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setWorkoutExercises(updated);
    setHasUnsavedChanges(true);
  };

  // Add actual set
  const addActualSet = (exerciseIndex) => {
    const updated = [...workoutExercises];
    const ex = updated[exerciseIndex];
    const lastSet = ex.actual_sets[ex.actual_sets.length - 1];
    updated[exerciseIndex].actual_sets.push({
      reps: lastSet?.reps || ex.target_reps || 12,
      weight: lastSet?.weight || ex.target_weight || 0,
      rest_time: lastSet?.rest_time || ex.rest_time || 120
    });
    setWorkoutExercises(updated);
    setHasUnsavedChanges(true);
  };

  // Update actual set
  const updateActualSet = (exerciseIndex, setIndex, field, value) => {
    const updated = [...workoutExercises];
    updated[exerciseIndex].actual_sets[setIndex][field] =
      field === 'rest_time' ? parseInt(value) || 0 : parseFloat(value) || 0;
    setWorkoutExercises(updated);
    setHasUnsavedChanges(true);
  };

  // Remove actual set
  const removeActualSet = (exerciseIndex, setIndex) => {
    const updated = [...workoutExercises];
    updated[exerciseIndex].actual_sets.splice(setIndex, 1);
    setWorkoutExercises(updated);
    setHasUnsavedChanges(true);
  };

  // Cardio: add a performed round (time-based)
  const addCardioRound = (exerciseIndex) => {
    const updated = [...workoutExercises];
    const ex = updated[exerciseIndex];
    if (!ex.actual_sets) ex.actual_sets = [];
    ex.actual_sets.push({
      reps: 1, weight: 0,
      time: ex.target_duration || 60,
      rest_time: ex.rest_time || 60,
    });
    setWorkoutExercises(updated);
    setHasUnsavedChanges(true);
  };

  // Cardio: update a round field (time accepts mm:ss, rest is seconds)
  const updateCardioRound = (exerciseIndex, setIndex, field, value) => {
    const updated = [...workoutExercises];
    updated[exerciseIndex].actual_sets[setIndex][field] =
      field === 'time' ? (mmssToSeconds(value) || 0) : (parseInt(value) || 0);
    setWorkoutExercises(updated);
    setHasUnsavedChanges(true);
  };

  // Apply template
  // When assigning to a client (non-template mode), use the CLIENT's
  // LAST-PERFORMED weight + reps for each exercise (NOT the template
  // author's values, NOT their PR). Anchor on what the client actually
  // did most recently — don't push toward a PR they may not be ready for.
  // Falls back to 0 / template-defined reps when no history exists.
  const applyTemplate = (template) => {
    if (isResultsMode) return;

    const templateExercises = template.exercises.map(ex => {
      // Look up full exercise data from library for image/animation URLs
      const libExercise = allExercises.find(e => e.id === ex.exercise_id);

      // Client-specific last-performed override
      const usage = !isTemplateMode ? exerciseUsage[ex.exercise_id] : null;
      const clientLastWeight = usage?.last_weight;
      const clientLastReps = usage?.last_reps;

      const resolvedWeight = isTemplateMode
        ? (ex.weight ?? ex.target_weight ?? 0)
        : (clientLastWeight != null ? clientLastWeight : 0);
      const resolvedReps = isTemplateMode
        ? (ex.reps ?? ex.target_reps ?? 12)
        : (clientLastReps && clientLastReps > 0
            ? clientLastReps
            : (ex.reps ?? ex.target_reps ?? 12));

      return {
        exercise_id: ex.exercise_id,
        exercise_name: ex.exercise_name,
        target_sets: ex.sets ?? ex.target_sets ?? 4,
        target_reps: resolvedReps,
        target_weight: resolvedWeight,
        rest_time: ex.rest_time || 120,
        instructions: ex.instructions || '',
        instructions_es: ex.instructions_es || '',
        image_url: ex.image_url || libExercise?.image_url || '',
        animation_webp: ex.animation_webp || libExercise?.animation_webp || '',
        muscle_groups: ex.muscle_groups || libExercise?.muscle_groups || [],
        difficulty: ex.difficulty || libExercise?.difficulty || '',
        equipment: ex.equipment || libExercise?.equipment || '',
        is_superset: ex.is_superset || false,
        superset_group: ex.is_superset ? (ex.superset_group || 'A') : (ex.superset_group || null),
        actual_sets: [],
        note: ex.note || ''
      };
    });
    setWorkoutExercises(templateExercises);
    setAppliedTemplateOrigin({
      template_id: template.id,
      template_name: template.name || template.template_name || 'Unnamed Template'
    });
    setShowTemplates(false);
    setHasUnsavedChanges(true);
  };

  // Save workout or template
  const handleSave = async () => {
    if (workoutExercises.length === 0 && !isResultsMode) {
      alert('Please add at least one exercise');
      return;
    }
    
    // Validate template name in template mode
    if (isTemplateMode && !templateName.trim()) {
      alert('Please enter a template name');
      return;
    }
    
    // Check for validation errors before saving
    if (Object.keys(validationErrors).length > 0) {
      const errorMessages = [];
      for (const [index, errors] of Object.entries(validationErrors)) {
        const exerciseName = workoutExercises[index]?.exercise_name || `Exercise ${parseInt(index) + 1}`;
        for (const [field, message] of Object.entries(errors)) {
          errorMessages.push(`${exerciseName}: ${message}`);
        }
      }
      alert('Please fix validation errors before saving:\n\n' + errorMessages.join('\n'));
      return;
    }

    setIsSaving(true);
    try {
      // Pass template name in template mode, appliedTemplateOrigin otherwise
      const sixthArg = isTemplateMode ? templateName.trim() : appliedTemplateOrigin;
      await onSave(booking, workout, workoutExercises, workoutTimes, mode, sixthArg);
      setHasUnsavedChanges(false);
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // CRITICAL: Finish workout handler - marks workout as permanently completed
  const handleFinishWorkout = async () => {
    if (!workout?.id) {
      alert('Please save the workout first before finishing.');
      return;
    }
    
    // Confirmation dialog
    const confirmed = window.confirm(
      'Are you sure you want to finish this workout?\n\n' +
      '⚠️ This action cannot be undone.\n' +
      '• You won\'t be able to edit the plan anymore\n' +
      '• Only execution data can be corrected after this'
    );
    
    if (!confirmed) return;
    
    setIsFinishing(true);
    try {
      // First save any pending changes
      if (hasUnsavedChanges) {
        await onSave(booking, workout, workoutExercises, workoutTimes, mode, appliedTemplateOrigin);
      }
      
      // Then finish the workout
      await axios.post(`${API}/workouts/${workout.id}/finish`);
      
      alert('✅ Workout finished successfully!');
      setHasUnsavedChanges(false);
      onClose();
      
      // Call the callback if provided
      if (onFinishWorkout) {
        onFinishWorkout(workout.id);
      }
    } catch (error) {
      console.error('Error finishing workout:', error);
      alert('Error finishing workout: ' + (error.response?.data?.detail || error.message || 'Unknown error'));
    } finally {
      setIsFinishing(false);
    }
  };

  const modeLabels = {
    plan: '✏️ Plan Workout',
    entry: '📝 Log Results',
    results: '📊 View Results',
    template: '📋 Edit Template'
  };

  const modeDescriptions = {
    plan: 'Design the workout plan. Add exercises, set targets, create supersets. Drag to reorder.',
    entry: 'Session occurred. Log execution data, add notes. Structure is locked.',
    results: 'Workout finished. Correction mode only. Structure is permanently locked.',
    template: 'Edit template structure. Add exercises, create supersets. Drag to reorder. Target parameters will be set when applied to a session.'
  };

  const modeColors = {
    plan: 'blue',
    entry: 'orange',
    results: 'green',
    template: 'purple'
  };

  const color = modeColors[mode] || 'blue';

  // Calculate total volume (check both new and legacy field names) - not shown in template mode
  const totalVolume = isTemplateMode ? 0 : workoutExercises.reduce((sum, ex) => {
    const sets = ex.actual_sets?.length > 0
      ? ex.actual_sets.reduce((s, set) => s + (set.weight * set.reps), 0)
      : ((ex.target_weight ?? ex.weight ?? 0) * (ex.target_sets ?? ex.sets ?? 0) * (ex.target_reps ?? ex.reps ?? 0));
    return sum + sets;
  }, 0);

  // Can finish workout? Only in entry mode (not plan, not already finished, not template)
  const canFinishWorkout = isEntryMode && workout?.id && !isTemplateMode;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col overflow-hidden">
      {/* Fixed Header */}
      <header className={`bg-${color}-900/80 border-b border-${color}-500/30 px-6 py-4 flex-shrink-0`}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-3">
              {modeLabels[mode]}
              {hasUnsavedChanges && (
                <span className="text-yellow-400 text-sm font-normal">• Unsaved changes</span>
              )}
              {Object.keys(validationErrors).length > 0 && (
                <span className="text-red-400 text-sm font-normal">• Validation errors</span>
              )}
              {workoutExercises.length > 0 && (
                <span className="text-amber-300 text-sm font-normal flex items-center gap-1" data-testid="editor-est-duration" title="Estimated duration">
                  🏁 ~{formatDuration(estimateWorkoutSeconds(workoutExercises))}
                </span>
              )}
            </h1>
            <p className={`text-${color}-300 text-sm mt-1`} data-testid="editor-booking-info">
              {isTemplateMode ? (
                templateName || 'New Template'
              ) : booking ? (
                `${booking.client_name} • ${booking.date} • ${booking.start_time} - ${booking.end_time}`
              ) : (
                workout?.template_name || 'New Workout'
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!isTemplateMode && clientId && clientId !== 'template' && (
              <button
                type="button"
                onClick={() => setShowMuscleBalance(true)}
                className="px-3 py-2 rounded-lg text-sm bg-red-900/40 border border-red-500/40 text-red-100 hover:bg-red-800/50 transition"
                title="Client muscle balance · last 4 weeks"
                data-testid="editor-muscle-balance-btn"
              >
                💪 4 sem
              </button>
            )}
            {/* Trainer-only: deload week marker (operational continuity signal).
                Persists immediately to workout.is_deload so the continuity
                tier is reflected on the next planner reload. Hidden in
                template mode (templates have no week to anchor). */}
            {!isTemplateMode && workout?.id && (
              <label
                className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition select-none ${
                  isDeload
                    ? 'bg-amber-500/20 border border-amber-400/50 text-amber-100'
                    : 'bg-slate-800/60 border border-slate-700 text-slate-300 hover:bg-slate-700/60'
                }`}
                title="Mark this session as part of a deload week. Halves the accumulated block on the continuity signal."
                data-testid="editor-deload-toggle"
              >
                <input
                  type="checkbox"
                  checked={isDeload}
                  onChange={async (e) => {
                    const next = e.target.checked;
                    setIsDeload(next);
                    try {
                      await axios.put(`${API}/workouts/${workout.id}`, { is_deload: next });
                    } catch (err) {
                      console.error('Error toggling is_deload:', err);
                      setIsDeload(!next); // revert on failure
                    }
                  }}
                  className="w-4 h-4 accent-amber-500"
                  data-testid="editor-deload-checkbox"
                />
                <span className="text-sm font-medium">🪶 Deload</span>
              </label>
            )}
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 transition"
            >
              ← Back
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || Object.keys(validationErrors).length > 0}
              className={`px-6 py-2 rounded-lg bg-${color}-600 hover:bg-${color}-500 text-white font-medium transition disabled:opacity-50 ${
                Object.keys(validationErrors).length > 0 ? 'cursor-not-allowed' : ''
              }`}
              data-testid="save-workout-btn"
              title={Object.keys(validationErrors).length > 0 ? 'Fix validation errors before saving' : ''}
            >
              {isSaving ? '⏳ Saving...' : isTemplateMode ? '💾 Save Template' : '💾 Save Workout'}
            </button>
          </div>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Template Name Input - Only in template mode */}
          {isTemplateMode && (
            <div className="mb-6">
              <label className="block text-purple-200 text-sm font-medium mb-2">Template Name *</label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => { setTemplateName(e.target.value); setHasUnsavedChanges(true); }}
                placeholder="e.g., Upper Body Push, Full Body Day A..."
                className="input-field w-full text-lg py-3"
                data-testid="template-name-input"
              />
            </div>
          )}
          
          {/* Mode Indicator */}
          <div className={`rounded-lg p-4 mb-6 bg-${color}-900/20 border border-${color}-500/20`}>
            <p className={`text-${color}-200`}>{modeDescriptions[mode]}</p>
          </div>

          {/* Stats Bar - Different for template mode */}
          {isTemplateMode ? (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="glass-card p-4 text-center">
                <p className="text-purple-300 text-xs uppercase tracking-wide">Exercises</p>
                <p className="text-2xl font-bold text-white">{workoutExercises.length}</p>
              </div>
              <div className="glass-card p-4 text-center">
                <p className="text-purple-300 text-xs uppercase tracking-wide">Supersets</p>
                <p className="text-2xl font-bold text-white">{workoutExercises.filter(ex => ex.is_superset).length}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="glass-card p-4 text-center" data-testid="stat-exercises">
                <p className="text-blue-300 text-xs uppercase tracking-wide">Exercises</p>
                <p className="text-2xl font-bold text-white">{workoutExercises.length}</p>
              </div>
              <div className="glass-card p-4 text-center" data-testid="stat-volume">
                <p className="text-blue-300 text-xs uppercase tracking-wide">Est. Volume</p>
                <p className="text-2xl font-bold text-white">{totalVolume.toLocaleString()} <span className="text-sm text-blue-400">kg</span></p>
              </div>
              <div className="glass-card p-4 text-center" data-testid="stat-duration">
                <p className="text-amber-300 text-xs uppercase tracking-wide">Est. Duration</p>
                <p className="text-2xl font-bold text-amber-200">
                  {workoutExercises.length > 0 ? `~${formatDuration(estimateWorkoutSeconds(workoutExercises))}` : '—'}
                </p>
              </div>
              <div className="glass-card p-4 text-center" data-testid="stat-supersets">
                <p className="text-blue-300 text-xs uppercase tracking-wide">Supersets</p>
                <p className="text-2xl font-bold text-white">{workoutExercises.filter(ex => ex.is_superset).length}</p>
              </div>
            </div>
          )}

          {/* Action Buttons (plan/template mode only - structure locked in entry/results) */}
          {canEditStructure && (
            <div className="flex gap-3 mb-6">
              {!isTemplateMode && (
                <button
                  onClick={() => setShowTemplates(!showTemplates)}
                  className="btn-secondary"
                >
                  📋 Use Template
                </button>
              )}
              <button
                onClick={() => setShowExerciseSelector(!showExerciseSelector)}
                className="btn-primary"
                data-testid="add-exercise-btn"
              >
                + Add Exercise
              </button>
            </div>
          )}
          
          {/* Structure Locked Notice */}
          {isStructureLocked && (
            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <p className="text-yellow-200 text-sm">
                🔒 <strong>Structure is locked.</strong> You can log execution data (sets, reps, weights) and add notes, 
                but cannot add, remove, or reorder exercises.
                {isEntryMode && (
                  <span className="block mt-2">
                    When finished, click <strong>&quot;✅ Finish Workout&quot;</strong> to mark this workout as completed.
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Template Selector - Enhanced Panel */}
          {showTemplates && !isStructureLocked && (
            <TemplateSelectorPanel
              templates={templates}
              onSelectTemplate={applyTemplate}
              onClose={() => setShowTemplates(false)}
            />
          )}

          {/* Exercise Selector moved to a focused modal overlay (below) */}

          {/* Exercise List */}
          <div className="space-y-4">
            {workoutExercises.map((exercise, index) => (
              <div
                key={index}
                draggable={canEditStructure}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                className={`glass-card p-5 border-l-4 transition-all ${
                  exercise.is_superset ? 'border-orange-400 bg-orange-900/10' : isTemplateMode ? 'border-purple-400' : 'border-blue-400'
                } ${canEditStructure ? 'cursor-move' : ''} ${draggedExercise === index ? 'opacity-50' : ''}`}
              >
                {/* Exercise Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-4">
                    {canEditStructure && (
                      <span className={`${isTemplateMode ? 'text-purple-400' : 'text-blue-400'} cursor-move`} title="Drag to reorder">⋮⋮</span>
                    )}
                    {(exercise.animation_webp || exercise.image_url) && (
                      <img
                        src={getExerciseAnimationUrl(exercise)}
                        alt={exercise.exercise_name}
                        className="w-14 h-14 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className={`${isTemplateMode ? 'text-purple-400 bg-purple-900/50' : 'text-blue-400 bg-blue-900/50'} font-mono text-sm px-2 py-0.5 rounded`}>
                          {index + 1}
                        </span>
                        <h4 className="text-white font-semibold text-lg">{exercise.exercise_name}</h4>
                        {exercise.is_superset && (
                          <span className="bg-orange-500/20 text-orange-200 px-2 py-1 rounded text-xs font-medium">
                            🔗 Superset {exercise.superset_group}
                          </span>
                        )}
                      </div>
                      <p className="text-blue-300 text-sm">{exercise.equipment}</p>
                      {/* P0-1: compact last-session hint inline */}
                      {!isTemplateMode && (
                        <LastSessionsHint
                          hint={lastSessionsHints?.[exercise.exercise_id]}
                          variant="compact"
                          exerciseName={exercise.exercise_name}
                        />
                      )}
                    </div>
                  </div>

                  {/* Exercise actions - structure editing (plan/template mode) */}
                  {canEditStructure && (
                    <div className="flex items-center space-x-2">
                      {/* Touch-friendly move up/down */}
                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => moveExercise(index, -1)}
                          disabled={index === 0}
                          className={`px-1.5 py-0.5 rounded text-xs transition ${index === 0 ? 'opacity-30 cursor-not-allowed' : 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30'}`}
                          data-testid={`move-up-${index}`}
                          title="Move up"
                        >
                          ▲
                        </button>
                        <button
                          onClick={() => moveExercise(index, 1)}
                          disabled={index === workoutExercises.length - 1}
                          className={`px-1.5 py-0.5 rounded text-xs transition ${index === workoutExercises.length - 1 ? 'opacity-30 cursor-not-allowed' : 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30'}`}
                          data-testid={`move-down-${index}`}
                          title="Move down"
                        >
                          ▼
                        </button>
                      </div>
                      <button
                        onClick={() => toggleSuperset(index)}
                        className={`text-sm px-3 py-1.5 rounded-lg transition ${
                          exercise.is_superset
                            ? 'bg-orange-500/30 text-orange-200 hover:bg-orange-500/40'
                            : isTemplateMode ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30' : 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30'
                        }`}
                      >
                        {exercise.is_superset ? '🔗 Superset' : '+ Superset'}
                      </button>
                      {exercise.is_superset && (
                        <select
                          value={exercise.superset_group || 'A'}
                          onChange={(e) => updateExercise(index, 'superset_group', e.target.value)}
                          className="text-sm px-2 py-1.5 rounded-lg bg-orange-900/30 text-orange-200 border border-orange-500/30"
                        >
                          {['A', 'B', 'C', 'D', 'E'].map(g => (
                            <option key={g} value={g}>Group {g}</option>
                          ))}
                        </select>
                      )}
                      <button
                        onClick={() => removeExercise(index)}
                        className="p-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 transition"
                        title="Remove exercise"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>

                {/* Target Parameters - HIDDEN in template mode */}
                {!isTemplateMode && !isCardio(exercise) && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-blue-200 text-xs font-medium mb-1">Target Sets</label>
                      <input
                        type="number"
                        min={VALIDATION_LIMITS.target_sets.min}
                        max={VALIDATION_LIMITS.target_sets.max}
                        value={exercise.target_sets ?? exercise.sets ?? 4}
                        onChange={(e) => updateExercise(index, 'target_sets', parseInt(e.target.value))}
                        disabled={isStructureLocked}
                        className={`w-full bg-blue-900/30 text-white border rounded-lg px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                          validationErrors[index]?.target_sets ? 'border-red-500' : 'border-blue-400/30'
                        }`}
                      />
                      {validationErrors[index]?.target_sets && (
                        <p className="text-red-400 text-xs mt-1">{validationErrors[index].target_sets}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-blue-200 text-xs font-medium mb-1">Target Reps</label>
                      <input
                        type="number"
                        min={VALIDATION_LIMITS.target_reps.min}
                        max={VALIDATION_LIMITS.target_reps.max}
                        value={exercise.target_reps ?? exercise.reps ?? 12}
                        onChange={(e) => updateExercise(index, 'target_reps', parseInt(e.target.value))}
                        disabled={isStructureLocked}
                        className={`w-full bg-blue-900/30 text-white border rounded-lg px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                          validationErrors[index]?.target_reps ? 'border-red-500' : 'border-blue-400/30'
                        }`}
                      />
                      {validationErrors[index]?.target_reps && (
                        <p className="text-red-400 text-xs mt-1">{validationErrors[index].target_reps}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-blue-200 text-xs font-medium mb-1">Target Weight (kg)</label>
                      <input
                        type="number"
                        min={VALIDATION_LIMITS.target_weight.min}
                        max={VALIDATION_LIMITS.target_weight.max}
                        step="0.5"
                        value={exercise.target_weight || exercise.weight || 0}
                        onChange={(e) => updateExercise(index, 'target_weight', parseFloat(e.target.value))}
                        disabled={isStructureLocked}
                        className={`w-full bg-blue-900/30 text-white border rounded-lg px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                          validationErrors[index]?.target_weight ? 'border-red-500' : 'border-blue-400/30'
                        }`}
                      />
                      {validationErrors[index]?.target_weight && (
                        <p className="text-red-400 text-xs mt-1">{validationErrors[index].target_weight}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-blue-200 text-xs font-medium mb-1">Rest (sec)</label>
                      <input
                        type="number"
                        min={VALIDATION_LIMITS.rest_time.min}
                        max={VALIDATION_LIMITS.rest_time.max}
                        step="15"
                        value={exercise.rest_time || 120}
                        onChange={(e) => updateExercise(index, 'rest_time', parseInt(e.target.value))}
                        disabled={isStructureLocked}
                        className={`w-full bg-blue-900/30 text-white border rounded-lg px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                          validationErrors[index]?.rest_time ? 'border-red-500' : 'border-blue-400/30'
                        }`}
                      />
                      {validationErrors[index]?.rest_time && (
                        <p className="text-red-400 text-xs mt-1">{validationErrors[index].rest_time}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Cardio target — duration per round instead of reps × weight */}
                {!isTemplateMode && isCardio(exercise) && (
                  <div className="grid grid-cols-3 gap-4 mb-4" data-testid={`cardio-target-${index}`}>
                    <div>
                      <label className="block text-cyan-200 text-xs font-medium mb-1">Rounds</label>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={exercise.target_sets ?? exercise.sets ?? 3}
                        onChange={(e) => updateExercise(index, 'target_sets', parseInt(e.target.value))}
                        disabled={isStructureLocked}
                        className="w-full bg-cyan-900/20 text-white border border-cyan-400/30 rounded-lg px-3 py-2 disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-cyan-200 text-xs font-medium mb-1">Duration / round (mm:ss)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="2:00"
                        value={secondsToMMSS(exercise.target_duration ?? 60)}
                        onChange={(e) => updateExercise(index, 'target_duration', mmssToSeconds(e.target.value) || 0)}
                        disabled={isStructureLocked}
                        className="w-full bg-cyan-900/20 text-white border border-cyan-400/30 rounded-lg px-3 py-2 disabled:opacity-50"
                        data-testid={`cardio-duration-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-cyan-200 text-xs font-medium mb-1">Rest (sec)</label>
                      <input
                        type="number"
                        min={0}
                        max={600}
                        step="15"
                        value={exercise.rest_time || 60}
                        onChange={(e) => updateExercise(index, 'rest_time', parseInt(e.target.value))}
                        disabled={isStructureLocked}
                        className="w-full bg-cyan-900/20 text-white border border-cyan-400/30 rounded-lg px-3 py-2 disabled:opacity-50"
                      />
                    </div>
                  </div>
                )}

                {/* Actual Sets (for entry/results mode - NOT in template mode) */}
                {isStructureLocked && !isTemplateMode && !isCardio(exercise) && (
                  <div className="bg-green-900/10 border border-green-500/20 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-green-300 text-sm font-medium">📊 Performed Sets</p>
                      <button
                        onClick={() => addActualSet(index)}
                        className="text-sm px-3 py-1 rounded bg-green-600/30 text-green-200 hover:bg-green-600/40 transition"
                      >
                        + Add Set
                      </button>
                    </div>
                    <div className="space-y-2">
                      {exercise.actual_sets?.map((set, setIdx) => (
                        <div key={setIdx} className="flex items-center gap-3 bg-green-900/20 p-3 rounded-lg">
                          <span className="text-green-400 text-sm font-medium w-16">Set {setIdx + 1}</span>
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="number"
                              value={set.reps}
                              onChange={(e) => updateActualSet(index, setIdx, 'reps', e.target.value)}
                              className="w-20 px-3 py-1.5 bg-green-900/30 border border-green-500/30 rounded text-white text-center"
                              placeholder="Reps"
                            />
                            <span className="text-green-300">×</span>
                            <input
                              type="number"
                              value={set.weight}
                              onChange={(e) => updateActualSet(index, setIdx, 'weight', e.target.value)}
                              className="w-24 px-3 py-1.5 bg-green-900/30 border border-green-500/30 rounded text-white text-center"
                              step="0.5"
                              placeholder="Weight"
                            />
                            <span className="text-green-300">kg</span>
                            <span className="text-green-400 text-sm ml-4">Rest:</span>
                            <input
                              type="number"
                              value={set.rest_time}
                              onChange={(e) => updateActualSet(index, setIdx, 'rest_time', e.target.value)}
                              className="w-20 px-3 py-1.5 bg-green-900/30 border border-green-500/30 rounded text-white text-center"
                              placeholder="Rest"
                            />
                            <span className="text-green-300">s</span>
                          </div>
                          <button
                            onClick={() => removeActualSet(index, setIdx)}
                            className="text-red-400 hover:text-red-300 p-1"
                            title="Remove set"
                          >
                            ✕
                          </button>
                        </div>
                      ))}

                      {(!exercise.actual_sets || exercise.actual_sets.length === 0) && (
                        <p className="text-green-400/60 text-sm italic py-2 text-center">
                          No sets recorded yet. Click &quot;+ Add Set&quot; to start logging.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Cardio performed rounds (entry/results mode) — time + rest */}
                {isStructureLocked && !isTemplateMode && isCardio(exercise) && (
                  <div className="bg-cyan-900/10 border border-cyan-500/20 rounded-lg p-4 mb-4" data-testid={`cardio-rounds-${index}`}>
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-cyan-300 text-sm font-medium">⏱ Performed Rounds</p>
                      <button
                        onClick={() => addCardioRound(index)}
                        className="text-sm px-3 py-1 rounded bg-cyan-600/30 text-cyan-200 hover:bg-cyan-600/40 transition"
                      >
                        + Add Round
                      </button>
                    </div>
                    <div className="space-y-2">
                      {exercise.actual_sets?.map((set, setIdx) => (
                        <div key={setIdx} className="flex items-center gap-3 bg-cyan-900/20 p-3 rounded-lg">
                          <span className="text-cyan-400 text-sm font-medium w-16">#{setIdx + 1}</span>
                          <div className="flex items-center gap-2 flex-1">
                            <span className="text-cyan-300 text-sm">Time</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="2:30"
                              value={secondsToMMSS(set.time)}
                              onChange={(e) => updateCardioRound(index, setIdx, 'time', e.target.value)}
                              className="w-24 px-3 py-1.5 bg-cyan-900/30 border border-cyan-500/30 rounded text-white text-center"
                            />
                            <span className="text-cyan-300 text-sm ml-4">Rest:</span>
                            <input
                              type="number"
                              value={set.rest_time}
                              onChange={(e) => updateCardioRound(index, setIdx, 'rest_time', e.target.value)}
                              className="w-20 px-3 py-1.5 bg-cyan-900/30 border border-cyan-500/30 rounded text-white text-center"
                              placeholder="Rest"
                            />
                            <span className="text-cyan-300">s</span>
                          </div>
                          <button
                            onClick={() => removeActualSet(index, setIdx)}
                            className="text-red-400 hover:text-red-300 p-1"
                            title="Remove round"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      {(!exercise.actual_sets || exercise.actual_sets.length === 0) && (
                        <p className="text-cyan-400/60 text-sm italic py-2 text-center">
                          No rounds recorded yet. Click &quot;+ Add Round&quot; to start logging.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Per-Exercise Notes - Hidden in template mode for simplicity */}
                {!isTemplateMode && (
                  <div className="mt-4">
                    <label className="block text-purple-300 text-xs font-medium mb-2">
                      📝 Exercise Notes / Observations
                    </label>
                    <textarea
                      value={exercise.note || ''}
                      onChange={(e) => updateExercise(index, 'note', e.target.value)}
                      className="w-full bg-purple-900/20 text-white border border-purple-400/30 rounded-lg px-3 py-2 text-sm placeholder-purple-400/50"
                      rows="2"
                      placeholder="Add notes, cues, pain observations, adjustments, reminders..."
                    />
                  </div>
                )}
              </div>
            ))}

            {workoutExercises.length === 0 && (
              <div className="glass-card p-12 text-center">
                <p className={`${isTemplateMode ? 'text-purple-300' : 'text-blue-300'} text-lg mb-4`}>
                  {isTemplateMode ? 'No exercises in this template yet' : 'No exercises added yet'}
                </p>
                {canEditStructure && (
                  <p className={`${isTemplateMode ? 'text-purple-400' : 'text-blue-400'} text-sm mb-4`}>
                    {isTemplateMode 
                      ? 'Click "+ Add Exercise" above to start building your template'
                      : 'Click "📋 Use Template" or "+ Add Exercise" above to get started'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Fixed Footer */}
      <footer className={`bg-slate-800 border-t border-slate-700 px-6 py-4 flex-shrink-0`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className={`${isTemplateMode ? 'text-purple-300' : 'text-blue-300'} text-sm`}>
            {workoutExercises.length} exercises
            {!isTemplateMode && ` • ${totalVolume.toLocaleString()} kg total volume`}
            {isTemplateMode && workoutExercises.filter(ex => ex.is_superset).length > 0 && ` • ${workoutExercises.filter(ex => ex.is_superset).length} in supersets`}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-6 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-6 py-2 rounded-lg bg-${color}-600 hover:bg-${color}-500 text-white font-medium transition disabled:opacity-50`}
            >
              {isSaving ? '⏳ Saving...' : isTemplateMode ? '💾 Save Template' : '💾 Save'}
            </button>
            
            {/* CRITICAL: Finish Workout Button - Only shown in entry mode */}
            {canFinishWorkout && (
              <button
                onClick={handleFinishWorkout}
                disabled={isFinishing}
                className="px-6 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white font-medium transition disabled:opacity-50 flex items-center gap-2"
              >
                {isFinishing ? '⏳ Finishing...' : '✅ Finish Workout'}
              </button>
            )}
          </div>
        </div>
      </footer>

      {/* Exercise Selector — focused modal over a frozen, dimmed background */}
      {showExerciseSelector && !isResultsMode && createPortal(
        <div
          className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm flex p-0 sm:p-4 overflow-hidden"
          data-testid="exercise-selector-modal"
          onClick={() => setShowExerciseSelector(false)}
        >
          <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
            <ExerciseSelectorGrid
              exercises={allExercises}
              onSelectExercise={addExercise}
              onClose={() => setShowExerciseSelector(false)}
              isTemplateMode={isTemplateMode}
              exerciseUsage={exerciseUsage}
              selectedExercises={workoutExercises}
              onMoveSelected={moveExercise}
              onRemoveSelected={removeExercise}
              fullScreen={true}
              t={t}
            />
          </div>
        </div>,
        document.body
      )}

      {showMuscleBalance && createPortal(
        <div
          className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="muscle-balance-modal"
          onClick={() => setShowMuscleBalance(false)}
        >
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <MuscleHeatmapCard clientId={clientId} language="es" defaultDays={28} title="Músculos (4 semanas)" />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default FullScreenWorkoutEditor;
